/**
 * Global Utils
 */
let stayAIsubscribers = {};

function stayAIsubscribe(eventName, callback) {
  if (stayAIsubscribers[eventName] === undefined) {
    stayAIsubscribers[eventName] = [];
  }

  stayAIsubscribers[eventName] = [...stayAIsubscribers[eventName], callback];

  return function stayAIunsubscribe() {
    stayAIsubscribers[eventName] = stayAIsubscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function stayAIpublish(eventName, data) {
  if (stayAIsubscribers[eventName]) {
    const promises = stayAIsubscribers[eventName]
      .map((callback) => callback(data))
    return Promise.all(promises);
  } else {
    return Promise.resolve()
  }
}

/**
 * Accessibility Utils
 */
const stayAItrapFocusHandlers = {};
function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  stayAItrapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', stayAItrapFocusHandlers.keydown);
  };

  stayAItrapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', stayAItrapFocusHandlers.keydown);
  };

  stayAItrapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;

    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', stayAItrapFocusHandlers.focusout);
  document.addEventListener('focusin', stayAItrapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', stayAItrapFocusHandlers.focusin);
  document.removeEventListener('focusout', stayAItrapFocusHandlers.focusout);
  document.removeEventListener('keydown', stayAItrapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function getFocusableElements(container) {

  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}


class StayAICartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#StayAICartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  /**
   * Sets event listener for cart icon on navbar.
   * Replace '#cart-icon-bubble' with current icon's class name or id
   */
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Opens cart drawer and traps focus based on triggering element.
   * @param {HTMLElement} triggerBy - Element that triggered this function (e.g. add to cart button)
   */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => { this.classList.add('animate', 'active');});

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.stay-ai-drawer__inner-empty')
          : document.getElementById('StayAICartDrawer');
        const focusElement = this.querySelector('.stay-ai-drawer__inner') || this.querySelector('.stay-ai-drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('stay-ai-overflow-hidden');
    
    // GWP: update when drawer opens
    if (window.gwpManager) {
      window.gwpManager.update();
    }
  }

  /**
   * Closes cart drawer and removes trapped focus based on triggering element.
   */
  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('stay-ai-overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /**
   * Re-renders cart content
   * @param {Object} parsedState - Cart state
   */
  async renderContents(parsedState) {
    const parsedStateId = parsedState?.id || parsedState?.items[0]?.id
    this.querySelector('.stay-ai-drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.stay-ai-drawer__inner').classList.remove('is-empty');
    this.productId = parsedStateId;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector('.stay-ai-drawer ' + section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#StayAICartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  /**
   * Selected section's inner HTML
   * @param {String} html - Cart state html
   * @param {String} selector - Element's class or id name
   * @returns {Element}
   */
  getSectionInnerHTML(html, selector) {
    if (!selector || !html) return
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    
    if (element) {
      return element.innerHTML
    } else {
      return document.getElementById(selector.substring(1)).innerHTML
    }
  }

  /**
   * Cart sections that are re-rendered
   */
  getSectionsToRender() {
    return [
      {
        id: 'stay-ai-cart-drawer',
        selector: '#StayAICartDrawer',
      },
    ];
  }

  /**
   * Opens cart drawer and traps focus based on triggering element.
   * @param {String} html - Cart state html
   * @param {String} selector - Element's class or id name
   * @returns {Element}
   */
  getSectionDOM(html, selector) {
    if (!selector || !html) return
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  /**
   * Sets active element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}
customElements.define('stay-ai-cart-drawer', StayAICartDrawer);

class StayAICartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('StayAICartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.loadEventListeners()
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  loadEventListeners() {
    const cartItemSubscriptionOptions = document.querySelectorAll('select[data-cart-selling-plan-toggle]');
    const cartItemSubscriptionButton = document.querySelectorAll('button[data-cart-selling-plan-toggle]');

    [...cartItemSubscriptionOptions].forEach((item) => {
      item.addEventListener(('change'), this.handleSellingPlanUpdate.bind(this))
    });
    [...cartItemSubscriptionButton].forEach((item) => {
      item.addEventListener('click', this.handleSellingPlanUpdate.bind(this))
    });
  }

  async handleSellingPlanUpdate(event) {
    event.preventDefault()
    const defaultSellingPlan = event.target.classList.contains('cart-item__selling-plan-subscribe')
    const parentElement = event.target.parentElement
    const variantKey = parentElement.dataset.variantKey
    const sellingPlan = defaultSellingPlan ? parentElement.dataset.availableSellingPlan : event.target.value

    await this.subscriptionToggle(variantKey, sellingPlan, event)
  }

  /**
   * Resets quantity input value
   * @param {String} id - Element index
   */
  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  /**
   * Check if quantity changes
   * @param {Event} event - Triggered event
   */
  onChange(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    
    this.updateQuantity(
      index,
      inputValue,
      event,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'StayAICartDrawer-CartItems',
        section: 'stay-ai-cart-drawer',
        selector: '.stay-ai-drawer__contents',
      }
    ];
  }

  /**
   * Toggle between selling plan
   * @param {String} keys - Individual cart keys
   * @param {String} sellingPlanID - selling plan ID
   * @param {Event} event - Triggered event
   */
  async subscriptionToggle(keys, sellingPlanID, event) {
    if (!keys && !sellingPlanID) return

    if (keys) {
      const keysArray = keys.split('|').filter(Boolean)
      const variantIDs = keysArray.map((key) => key.split(':')[0])
      if (keysArray.length > 1) await this.deleteProducts(keysArray);

      await this.updateSellingPlan(keysArray, sellingPlanID, event)
    }
  }

  /**
   * Deletes products when updating selling plans on bundles.
   * *This is a solution to Shopify's inability to do batch cart updates.
   * @param {Array} keys - Cart keys
   */
  async deleteProducts(keys) {
    const updates = keys.reduce((acc, curr) => {
      acc[curr.toString()] = 0;
      return acc;
    }, {});

    const updateCart = await (await fetch(`/cart/update.js`, {
      method: "POST",
      headers: {
          "Content-Type": "application/json"
      },
      body: JSON.stringify({updates})
    })).json();
  }

  /**
   * Update selling plan on selected products in cart
   * @param {Array} keys - Cart keys
   * @param {String} sellingPlanID - selling plan ID
   * @param {Event} event - Triggered event
   */
  async updateSellingPlan(keys, sellingPlanID, event) {
    const mainEl = event.target.parentElement
    const itemQty = mainEl.dataset.cartQuantity
    const quantity = itemQty.split('|')
    const bundleMinMax = mainEl.dataset.bundleMinMax
    const bundleParent = mainEl.dataset.bundleParent
    const bundleID = this.getGuid();
    let drawer = document.querySelector('stay-ai-cart-drawer');
    let body = {}

    body = keys.length === 1 
    ? {
      'id': keys[0],
      'quantity': this.targetQuantity,
      'selling_plan': sellingPlanID
    }
    : {
      'items': keys.map((key, index) => {
        const id = key.split(':')[0]
        const data = {
          'id': id,
          'quantity': quantity[index],
          'selling_plan': sellingPlanID,
          'properties': {
            _bundle_min: bundleMinMax.split('|')[0],
            _bundle_max: bundleMinMax.split('|')[1],
            _bundle_id: bundleID
          }
        }
        if (id === bundleParent) {data.properties['_bundle_parent']= true}
        return data
      })
    }

    body.sections = drawer.getSectionsToRender().map((section) => section.id),
    body.section_url = window.location.pathname
    body = JSON.stringify(body)

    const route = keys.length > 1 ? stayAIRoutes.cart_add_url : stayAIRoutes.cart_change_url
    fetch(`${route}`, { ...fetchConfig(), ...{ body} })
      .then((response) => {
        return response.json();
      })
      .then((parsedState) => {
        if (drawer) {
          drawer.renderContents(parsedState);
        }
      })
      .catch((error) => {
        console.error("Error adding product to cart:", error);
      });
  }

  /**
   * Get unique ID for Bundle products
   * @returns {String}
   */
  getGuid() {
    let d = new Date().getTime();
    let d2 =  (performance && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      let r = Math.random() * 16;
      if (d > 0) {
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c == 'x' ? r: (r & 0x7 | 0x8)).toString(16);
    });
  }

  /**
   * Update product quantity with updated value
   * @param {String} line - Order of quantity element
   * @param {Number} quantity - Updated quantity
   * @param {Object} event - triggered event
   * @param {String} name - Name of selected element
   * @param {String} variantId - Variant ID
   */
  updateQuantity(line, quantity, event, name, variantId) {
    if (!line) return
    
    this.enableLoading();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${stayAIRoutes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Stay-AI-Drawer-quantity-${line}`);

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('.stay-ai-drawer');

        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        
        // If cart is empty, we need to render the entire drawer to show "Continue shopping" button
        if (parsedState.item_count === 0) {
          const drawer = document.querySelector('stay-ai-cart-drawer');
          if (drawer) {
            drawer.renderContents(parsedState);
            return;
          }
        }
        
        this.getSectionsToRender().forEach((section) => {
          let elementToReplace;
          
          // If section has an ID, try to find by ID first
          if (section.id) {
            const parent = document.getElementById(section.id);
            elementToReplace = parent?.querySelector(section.selector) || parent;
          }
          
          // If no element found and we have a selector, try finding directly by selector
          if (!elementToReplace && section.selector) {
            elementToReplace = document.querySelector(section.selector);
          }
          
          if (!elementToReplace) {
            console.error('GWP: Element not found for section:', section);
            return;
          }
          
          const sectionHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
          if (sectionHTML) {
            elementToReplace.innerHTML = sectionHTML;
          }
        });

        const lineItem = document.getElementById(`StayAICartDrawer-Item-${line}`);
        
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.stay-ai-drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.stay-ai-drawer .stay-ai-cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.stay-ai-drawer .stay-ai-cart-item__name'));
        }

        this.updateCartCount(parsedState.item_count)
        this.checkOrphanBundleItems(parsedState.items)
        
        // GWP: update after qty change
        if (window.gwpManager) {
          window.gwpManager.update()
        }
      })
      .catch((error) => {
        const errors = document.getElementById('cart-errors') || document.getElementById('StayAICartDrawer-CartErrors');
        errors.textContent = 'Error updating your cart'
      })
      .finally(() => {
        this.disableLoading();
      });
  }

  updateCartCount(total, cartBody = null) {
    const cartHeaderCount = document.querySelector('#cart-icon-btn .Header__CartCount');

    if (!cartHeaderCount) return

    if (total) {
      cartHeaderCount.textContent = total;
      return
    }

    const cartItems = cartBody.querySelectorAll('.stay-ai-cart-item');

    const totalProducts = [...cartItems]?.reduce((acc, product) => {
      const productsTotal = product.querySelector('stay-ai-quantity-input .quantity__input')?.value || 0
      return acc + Number(productsTotal)
    }, 0);

    cartHeaderCount.textContent = totalProducts || 0
  }

  /**
   * Update product quantity with updated value
   * @param {String} line - Order of quantity element
   * @param {String} message - Error message to display
   */
  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`StayAICartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('StayAICartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  /**
   * Selected section's inner HTML
   * @param {String} html - Cart state html
   * @param {String} selector - Element's class or id name
   * @returns {Element}
   */
  getSectionInnerHTML(html, selector) {
    if (!html || !selector) return
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  /**
   * Temporarily disables menu cart item on update
   */
  enableLoading() {
    const mainCartItems = document.getElementById('StayAICartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  /**
   * Enables temporarily disabled menu cart items
   */
  disableLoading() {
    const mainCartItems = document.getElementById('StayAICartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');
  }

  /**
   * Checks for unlinked bundle items in cart
   * @param {Array} lineItems - Cart items
   */
  checkOrphanBundleItems = (lineItems) => {
    if (!lineItems || lineItems.length <= 0) return;
  
    let bundleItems = [...lineItems].map((item) => item?.properties?._bundle_id);
    let bundleIDs = bundleItems.filter((id) => id);
    let orphanBundleIDs = [];
    if (!bundleIDs || bundleIDs.length <= 0) return;
  
    bundleIDs.forEach((bundleID) => {
      let bundleMin = 3;
      let bundleMax = 20;
      let bundleCount = 0;
      const hasChildProduct = lineItems.filter((item) =>
        item.properties?._bundle_id == bundleID && !item?.properties?._bundle_parent,
      );
      const hasParentProduct = lineItems.find( (item) =>
        item.properties?._bundle_id == bundleID && item?.properties?._bundle_parent === true,
      );
  
      if (!hasChildProduct || hasChildProduct.length <= 0 || !hasParentProduct) {
        orphanBundleIDs.push(bundleID);
        return;
      }
  
      bundleMin = parseInt(hasParentProduct.properties?._bundle_min) || bundleMin;
      bundleMax = parseInt(hasParentProduct.properties?._bundle_max) || bundleMax;
  
      bundleCount = hasChildProduct.reduce((acc, item) => acc + parseInt(item.quantity), 0) || bundleCount;
      if (bundleCount < bundleMin || bundleCount > bundleMax) {
        orphanBundleIDs.push(bundleID);
      }
    });
  
    if (orphanBundleIDs.length <= 0) return;
  
    this.deleteOrphanBundleProducts(lineItems, orphanBundleIDs);
  };

  /**
   * Removes all unlinked bundle products
   * @param {Array} cart - Cart line items
   * @param {Array} orphanBundleIDs - Array of unlinked products to delete
   */
  deleteOrphanBundleProducts(cart, orphanBundleIDs) {
    let drawer = document.querySelector('stay-ai-cart-drawer');
    let orphanData = {
      updates: {},
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    if (drawer) {
      orphanData.sections = drawer.getSectionsToRender().map((section) => section.id);
    }

    orphanBundleIDs.forEach((bundleID) => {
      cart.map((item) => {
        if (!item || item?.properties?._bundle_id !== bundleID) {
          return;
        }
  
        orphanData["updates"][String(item.key)] = 0;
      });
    });

    fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "xmlhttprequest",
      },
      body: JSON.stringify(orphanData),
    })
    .then((response) => {
      return response.json();
    })
    .then((state) => {
      if (drawer) {
        drawer.renderContents(state);
      }
    })
    .catch((error) => {
      console.error("Error removing bundle product from cart:", error);
    });
  }
}
customElements.define('stay-ai-cart-items', StayAICartItems);

class StayAICartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('stay-ai-cart-items') || this.closest('stay-ai-cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}
customElements.define('stay-ai-cart-remove-button', StayAICartRemoveButton);

class StayAICartDrawerItems extends StayAICartItems {
  getSectionsToRender() {
    return [
      {
        id: 'StayAICartDrawer-CartItems',
        section: 'stay-ai-cart-drawer',
        selector: '.stay-ai-drawer__contents',
      },
      {
        section: 'stay-ai-cart-drawer',
        selector: '.stay-ai-drawer__footer',
      }
    ];
  }
}
customElements.define('stay-ai-cart-drawer-items', StayAICartDrawerItems);

class StayAIQuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('.quantity__button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  connectedCallback() {
    this.validateQtyRules();
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('stay-ai-quantity-input', StayAIQuantityInput);

/**
 * ============================================
 *  GWP MANAGER - Gift With Purchase System
 * ============================================
 */
class GWPManager {
  /**
   * Initialize GWP Manager
   * Loads configuration and sets up event listeners
   */
  constructor() {
    this.config = window.gwpConfig || { tiers: [] };
    this.selectedGifts = new Map();
    this.flickityInstance = null; // Store Flickity carousel instance
    if (!this.config.tiers || this.config.tiers.length === 0) return;
  }

  /**
   * Main update function
   * Fetches cart, calculates tiers, and renders GWP UI
   */
  async update() {
    try {
      const container = document.getElementById('GWPContainer');
      if (!container) return;

      const cart = await fetch('/cart.js').then(r => r.json());
      const total = this.getCartTotal(cart);
      
      let currentTier = null;
      let nextTier = null;
      
      for (const tier of this.config.tiers) {
        if (total >= tier.threshold) {
          currentTier = tier;
        } else if (!nextTier) {
          nextTier = tier;
          break;
        }
      }
      
      this.render(currentTier, nextTier, total, cart);
      await this.removeUnqualifiedGifts(cart, total, currentTier);
    } catch (error) {
      console.error('GWP Error:', error);
    }
  }

  /**
   * Calculate cart total excluding GWP gifts
   * @param {Object} cart - Cart object from /cart.js
   * @returns {Number} Total in cents
   */
  getCartTotal(cart) {
    let total = 0;
    cart.items.forEach(item => {
      if (item.properties?._gwp_gift !== 'true') {
        total += item.final_line_price;
      }
    });
    return total;
  }

  /**
   * Render GWP UI
   * Shows current tier status, progress bar, and gift selector
   * @param {Object} currentTier - Currently unlocked tier
   * @param {Object} nextTier - Next tier to unlock
   * @param {Number} total - Cart total in cents
   * @param {Object} cart - Cart object
   */
  render(currentTier, nextTier, total, cart) {
    const container = document.getElementById('GWPContainer');
    if (!container) return;
    
    const existingProgressFill = container.querySelector('.gwp-progress-fill');
    const currentWidth = existingProgressFill ? parseFloat(existingProgressFill.style.width) || 0 : 0;
    
    let html = '';
    let progressHtml = '';
    let nextTierMessage = '';

    // Build progress bar with ALL tiers visible
    if (this.config.tiers && this.config.tiers.length > 0) {
      const maxThreshold = this.config.tiers[this.config.tiers.length - 1].threshold;
      let currentProgress;
      if (total >= maxThreshold) {
        currentProgress = 100;
      } else {
        currentProgress = Math.min(100, (total / maxThreshold) * 100);
      }
      
      // Build tier markers for the progress bar
      const tierMarkers = this.config.tiers.map((tier, index) => {
        const position = (tier.threshold / maxThreshold) * 100;
        const isUnlocked = total >= tier.threshold;
        const tierIcon = tier.type === 'free_shipping' ? 'https://cdn.shopify.com/s/files/1/1174/9088/files/truck-white.png?v=1763941500' : 'https://cdn.shopify.com/s/files/1/1174/9088/files/gift-white.png?v=1763941500';
        const isLastTier = index === this.config.tiers.length - 1;
        
       // Extract just the tier name from the message (e.g., "Free Shipping" from "FREE Shipping Unlocked!")
        const tierName = tier.name || 'Gift!';

        // For the last tier, align to right instead of center to keep it within bounds
        const alignmentStyle = isLastTier ? 'right: 0; transform: translate(0, -50%);' : `left: ${position}%;`;
        
        // Wrap icon in div for last tier to allow left alignment
        const iconHtml = isLastTier 
          ? `<div class="gwp-tier-icon-container--last"><span class="gwp-tier-icon"><img src="${tierIcon}" width="28" height="28" alt="Tier Icon" /></span></div>`
          : `<span class="gwp-tier-icon"><img src="${tierIcon}" width="28" height="28" alt="Tier Icon" /></span>`;
        
        return `
          <div class="gwp-tier-marker ${isUnlocked ? 'unlocked' : ''} ${isLastTier ? 'gwp-tier-marker--last' : ''}" 
               style="${alignmentStyle};" 
               title="${tier.message}">
            <span class="gwp-tier-threshold">${this.formatMoney(tier.threshold)}</span>
            ${iconHtml}
            <span class="gwp-tier-label">${tierName}</span>
          </div>
        `;
      }).join('');
      
      progressHtml = `
        <div class="gwp-progress-bar">
          <div class="gwp-progress-track">
            <div class="gwp-progress-fill" style="width: ${currentWidth}%" data-target-width="${currentProgress}"></div>
            ${tierMarkers}
          </div>
        </div>
      `;
      
      // Message for next tier
      if (nextTier) {
        const remaining = nextTier.threshold - total;
        nextTierMessage = nextTier.progressMessage.replace('{amount}', this.formatMoney(remaining));
      } else if (currentTier) {
        nextTierMessage = 'ALL REWARDS UNLOCKED!';
      }
    }

    // Always show progress bar first (at the top)
    if (nextTierMessage) {
      html += `<div class="gwp-progress-text ${!nextTier ? 'unlocked' : ''}">${nextTierMessage}</div>`;
    }
    html += progressHtml;

    // Then show current tier messages and gift selectors below
    if (currentTier) {
      if (currentTier.type === 'free_shipping') {
        const message = `✓ ${currentTier.message}`;
        html += `<div class="gwp-completed-tier"><div class="gwp-completed-text">${message}</div></div>`;
      } else if (currentTier.type === 'gift_product') {
        const giftInCart = cart.items.find(item => 
          item.properties?._gwp_gift === 'true' && 
          parseInt(item.properties?._gwp_tier_index) === currentTier.tierIndex
        );
        
        // Show gift selector below progress bar
        const statusMessage = giftInCart 
          ? `✓ ${currentTier.message} - Gift Added!`
          : `✓ ${currentTier.message}`;
        
        html += this.renderGiftSelector(currentTier, statusMessage, '', giftInCart);
      }
    }

    container.innerHTML = html;
    container.style.display = html ? 'block' : 'none';

    // Destroy previous Flickity instance if exists
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
      this.flickityInstance = null;
    }

    // Animate progress bar if needed
    const progressFill = container.querySelector('.gwp-progress-fill');
    if (progressFill) {
      const targetWidth = parseFloat(progressFill.dataset.targetWidth);
      
      // If all tiers unlocked or animation needed
      if (!nextTier || currentWidth !== targetWidth) {
        requestAnimationFrame(() => {
          progressFill.style.width = targetWidth + '%';
        });
      }
    }

    // Initialize Flickity for gift selector if present (always show for gift_product tiers)
    if (currentTier?.type === 'gift_product') {
      this.attachListeners(currentTier, cart);
      this.initFlickity();
    }
  }

  /**
   * Initialize Flickity carousel for gift products
   * Follows the theme's Carousel component pattern
   */
  initFlickity() {
    const slider = document.querySelector('.gwp-product-slider');
    if (!slider) {
      console.log('GWP: No slider found');
      return;
    }
    
    // Wait for next frame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (typeof Flickity !== 'undefined') {
        try {
          // Define config directly instead of parsing from attribute
          const config = {
            prevNextButtons: true,
            pageDots: false,
            wrapAround: false,
            contain: true,
            cellAlign: 'left',
            watchCSS: false, // Disable watchCSS to force initialization
            dragThreshold: 8,
            groupCells: false,
            arrowShape: { x0: 20, x1: 60, y1: 40, x2: 60, y2: 35, x3: 25 }
          };
          
          console.log('GWP: Initializing Flickity');
          this.flickityInstance = new Flickity(slider, config);
          console.log('GWP: Flickity initialized successfully');
        } catch (error) {
          console.error('GWP Flickity Error:', error);
        }
      } else {
        console.error('GWP: Flickity is not defined. Make sure Flickity library is loaded.');
      }
    });
  }

  /**
   * Render gift product selector
   * @param {Object} tier - Tier configuration
   * @param {String} combinedMessage - Combined status and progress message
   * @param {String} progressHtml - Progress bar HTML
   * @param {Object} giftInCart - Gift item currently in cart (if any)
   * @returns {String} HTML string for gift selector
   */
  renderGiftSelector(tier, combinedMessage, progressHtml, giftInCart) {
    if (!tier.products?.length) return '';

    console.log(tier.products.length)
    
    const productsHtml = tier.products.map(p => {
      // Mark the product as selected if it's the current gift in cart
      const isSelected = giftInCart && parseInt(giftInCart.variant_id) === parseInt(p.variantId);
      return `
        <div class="Carousel__Cell gwp-product-card ${tier.products.length == 1 ? 'only' : ''} ${isSelected ? 'selected' : ''}" data-variant="${p.variantId}">
          <img src="${p.image}" alt="${p.title}" class="gwp-product-image">
          <div class="gwp-product-title">${p.title}</div>
        </div>
      `;
    }).join('');
    
    // Button logic: if gift is in cart AND it's the selected one, disable button
    const hasSelectedGiftInCart = giftInCart && tier.products.some(p => 
      parseInt(giftInCart.variant_id) === parseInt(p.variantId)
    );
    
    const buttonText = hasSelectedGiftInCart ? 'GIFT IN CART' : 'SELECT A GIFT';
    const buttonDisabled = hasSelectedGiftInCart ? 'disabled' : '';
    
    return `
      <div class="gwp-unlocked">
        <div class="gwp-unlocked-title">${combinedMessage}</div>
        ${progressHtml}
        <div class="gwp-product-slider Carousel">
          ${productsHtml}
        </div>
        <button class="gwp-add-button Button Button--primary" data-tier="${tier.tierIndex}" ${buttonDisabled}>${buttonText}</button>
      </div>
    `;
  }

  /**
   * Attach click listeners to gift products and add button
   * @param {Object} tier - Tier configuration
   * @param {Object} cart - Current cart state
   */
  attachListeners(tier, cart) {
    const products = document.querySelectorAll('.gwp-product-card');
    const button = document.querySelector('.gwp-add-button');
    if (!button) return;

    // Check if there's already a gift in cart for this tier
    const giftInCart = cart.items.find(item => 
      item.properties?._gwp_gift === 'true' && 
      parseInt(item.properties?._gwp_tier_index) === tier.tierIndex
    );

    // Pre-select the gift if it's already in cart
    if (giftInCart) {
      this.selectedGifts.set(tier.tierIndex, giftInCart.variant_id.toString());
    }

    products.forEach(product => {
      product.addEventListener('click', (e) => {
        // Prevent Flickity drag from triggering selection
        if (this.flickityInstance && this.flickityInstance.isDragging) return;
        
        const clickedVariantId = product.dataset.variant;
        const isAlreadyInCart = giftInCart && giftInCart.variant_id.toString() === clickedVariantId;
        
        // If clicking on the same gift that's already in cart, do nothing
        if (isAlreadyInCart) {
          return;
        }
        
        // Update selection
        products.forEach(p => p.classList.remove('selected'));
        product.classList.add('selected');
        this.selectedGifts.set(tier.tierIndex, clickedVariantId);
        
        // Enable button and change text to "CHANGE GIFT" (since user selected different gift)
        button.disabled = false;
        button.textContent = giftInCart ? 'CHANGE GIFT' : 'ADD TO CART';
      });
    });

    button.addEventListener('click', () => this.addGift(tier.tierIndex, button, giftInCart));
  }

  /**
   * Add selected gift to cart (or replace existing gift)
   * @param {Number} tierIndex - Tier index
   * @param {HTMLElement} button - Add button element
   * @param {Object} existingGift - Existing gift in cart (if any)
   */
  async addGift(tierIndex, button, existingGift) {
    const variantId = this.selectedGifts.get(tierIndex);
    if (!variantId) return;

    button.disabled = true;
    button.textContent = 'ADDING...';

    try {
      // If there's an existing gift, remove it first
      if (existingGift) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingGift.key,
            quantity: 0
          })
        });
      }

      // Add the new gift
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ 
            id: variantId, 
            quantity: 1, 
            properties: { 
              _gwp_gift: 'true', 
              _gwp_tier_index: tierIndex.toString() 
            } 
          }]
        })
      });
      
      // Trigger product:added event so theme.min.js updates the drawer
      document.dispatchEvent(new CustomEvent('product:added'));
      
      button.textContent = existingGift ? 'CHANGED!' : 'ADDED!';
      button.disabled = true; // Keep disabled after adding
      
      // After delay, change to "GIFT IN CART"
      setTimeout(() => {
        button.textContent = 'GIFT IN CART';
        button.disabled = true;
      }, 1500);
    } catch (error) {
      console.error('GWP Error adding gift:', error);
      button.disabled = false;
      button.textContent = 'ERROR';
    }
  }

  /**
   * Remove gifts from cart that are no longer qualified
   * @param {Object} cart - Cart object
   * @param {Number} total - Cart total in cents
   * @param {Object} currentTier - Currently unlocked tier
   * @returns {Boolean} True if gifts were removed
   */
  async removeUnqualifiedGifts(cart, total, currentTier) {
    const giftsToRemove = [];
    
    // Find all gifts in cart
    cart.items.forEach(item => {
      if (item.properties?._gwp_gift === 'true') {
        const giftTierIndex = parseInt(item.properties._gwp_tier_index);
        const giftTier = this.config.tiers.find(t => t.tierIndex === giftTierIndex);
        
        // Check if this gift's tier is still unlocked
        if (!giftTier || total < giftTier.threshold) {
          giftsToRemove.push(item.key);
          console.log('GWP: Removing gift from tier', giftTierIndex, '- no longer qualified');
        }
      }
    });

    // Remove unqualified gifts
    if (giftsToRemove.length > 0) {
      const updates = {};
      giftsToRemove.forEach(key => {
        updates[key] = 0;
      });

      try {
        // Update the cart and get the new state
        const updatedCart = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        }).then(r => r.json());
        
        // Check if cart is now empty (only had gifts, no regular products)
        if (updatedCart.item_count === 0) {
          // Fetch full cart sections to render empty state
          const drawer = document.querySelector('stay-ai-cart-drawer');
          if (drawer) {
            const sectionsToFetch = drawer.getSectionsToRender().map(s => s.id).join(',');
            const response = await fetch(`${window.location.pathname}?sections=${sectionsToFetch}`);
            const sections = await response.json();
            
            const parsedState = {
              ...updatedCart,
              sections: sections
            };
            
            drawer.renderContents(parsedState);
          }
        } else {
          // Cart still has items, just trigger normal update
          document.dispatchEvent(new CustomEvent('product:added'));
        }
        
        return true; // Gift was removed
      } catch (error) {
        console.error('Error removing unqualified gifts:', error);
      }
    }
    
    return false; // No gifts removed
  }

  /**
   * Format cents to dollar string
   * @param {Number} cents - Amount in cents
   * @returns {String} Formatted money string
   */
  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

// Initialize GWP Manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { window.gwpManager = new GWPManager(); });
} else {
  window.gwpManager = new GWPManager();
}